/** @jsxImportSource @emotion/react */
import { useParams } from "react-router-dom";
import { routes } from "../../../../appRoutingHelpers";
import { TableRow } from "../../../../components/table-components/TableRow";
import { TableCellLink } from "../../../../components/table-components/TableCellLink";
import { StyledText } from "../../../../assets/typography/typography";
import { colors } from "../../../../components/colors";
import { AppCallToAction } from "../../../../service/api-response/AppCallToAction";
import { firstTableCellStyle } from "../../../../style-util";
import { Avatar } from "../../../../components/Avatar";
import { TableEllipsisDropdown } from "../../../../components/TableEllipsisDropdown";
import { DropdownItemButton } from "../../../common/DropdownItemButton";
import { DropdownItemLink } from "../../../common/DropdownItemLink";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { useDeleteCta } from "../../../../service/api";
import { toastr } from "react-redux-toastr";
import { getErrorMessage } from "../../../../error-handler";

type Props = {
  cta: AppCallToAction;
};

export const CtasListRow = ({ cta }: Props) => {
  const { organization: organizationKeyName } = useParams<{
    organization: string;
  }>();

  const { mutateAsync: deleteCta, isLoading: isDeleting } = useDeleteCta();

  const href = routes.ctasEdit(organizationKeyName, cta.id);

  const formatCTAType = () => {
    switch (cta.action) {
      case "discount_card":
        return "Member card CTA";
      case "punch_card":
        return "Punch card CTA";
      case "add_to_order":
        return "Add to order CTA";
      case "table_booking":
        return "Table booking CTA";
      default:
        return cta.action;
    }
  };

  return (
    <TableRow>
      <TableCellLink href={href} css={firstTableCellStyle}>
        <Avatar imageUrl={cta.imageUrl}>
          {cta.title.charAt(0).toUpperCase()}
        </Avatar>
      </TableCellLink>
      <TableCellLink href={href}>
        <StyledText className="bodyTextRegular mb-0" color={colors.textPrimary}>
          {cta.title}
        </StyledText>
      </TableCellLink>
      <TableCellLink href={href}>
        <StyledText className="bodyTextRegular mb-0" color={colors.textPrimary}>
          {cta.countryCode}
        </StyledText>
      </TableCellLink>
      <TableCellLink href={href}>
        <StyledText className="bodyTextRegular mb-0" color={colors.textPrimary}>
          {cta.locale}
        </StyledText>
      </TableCellLink>
      <TableCellLink href={href}>
        <StyledText className="bodyTextRegular mb-0" color={colors.textPrimary}>
          {formatCTAType()}
        </StyledText>
      </TableCellLink>
      <TableEllipsisDropdown
        menu={{
          items: [
            {
              key: "1",
              label: (
                <DropdownItemLink
                  icon={
                    <FontAwesomeIcon
                      icon={solid("arrow-up-right-from-square")}
                    />
                  }
                  to={href}
                >
                  Edit
                </DropdownItemLink>
              ),
            },
            {
              key: "2",
              label: (
                <DropdownItemButton
                  icon={<FontAwesomeIcon icon={solid("trash")} />}
                  disabled={isDeleting}
                  onClick={async () => {
                    try {
                      await deleteCta({
                        data: {
                          id: cta.id,
                        },
                        organizationKeyName: organizationKeyName!,
                      });
                      toastr.success("Success", "Cta deleted succesfully");
                    } catch (error) {
                      const errorMessage = getErrorMessage(error);
                      toastr.error("Error", errorMessage);
                    }
                  }}
                >
                  Delete cta
                </DropdownItemButton>
              ),
            },
          ],
        }}
      />
    </TableRow>
  );
};
